/**
 * Gated HITL bridge for `send_structure_set_to_pacs` — sends the current
 * structure set, with its latest edits, to a PACS exactly as the Export
 * dialog's "Export to PACS" does.
 *
 * Flow: parse → require connector mode and a loaded structure set → pick
 * the PACS from the outbound targets (the dialog's filter: Out or InOut) →
 * pick the structures (named ones, or the dialog's default: the approved
 * structures if any, otherwise every structure that is not rejected) → a
 * {@link SendStructureSetProposal} the card renders. Nothing leaves the
 * viewer until the clinician Applies.
 *
 * The existing send reports failures only as a viewer message, so the
 * outcome is read from the structure set's "sent" flag, which the viewer
 * sets only after a successful upload: unset → failed; newly set →
 * confirmed; already set before → applied but unconfirmed (the clinician is
 * asked to check the viewer message). Pure/React-free; ports are injected.
 */
import { resolveRoi } from '../core/roiResolver';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    reasonOf,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
} from '../core';
import type { ListedStructure, SendStructureSetOutcome } from '../../contract';

/** Same cap as the server contract (MAX_STRUCTURES_PER_SEND). */
export const MAX_STRUCTURES_PER_SEND = 100;

export interface ExportRoiFacts {
    readonly roiNumber: number;
    readonly name: string;
    readonly type: string;
    readonly isApproved: boolean;
    readonly isRejected: boolean;
}

export interface ExportStructureSetFacts {
    readonly uid: string;
    readonly label: string;
    readonly rois: ReadonlyArray<ExportRoiFacts>;
}

export interface SendStructureSetDeps extends PendingFlag {
    readonly connectorMode: boolean;
    /** The active structure set, or undefined when none is loaded. */
    structureSet(): ExportStructureSetFacts | undefined;
    /** PACS targets a structure set can be sent to (Out or InOut). */
    outboundTargets(): Promise<string[]>;
    /** The structure set's "sent" flag; undefined when it is gone. */
    isMarkedSent(uid: string): boolean | undefined;
    /** The Export dialog's send (`handleExportBatch`) for one set. */
    send(uid: string, roiNumbers: number[], targetName: string): Promise<void>;
}

export interface SendStructureSetProposal {
    readonly toolCallId: string;
    readonly structureSetLabel: string;
    readonly targetName: string;
    readonly structureNames: ReadonlyArray<string>;
    /** True when the names came from the dialog default, not the request. */
    readonly isDefaultSelection: boolean;
    apply(): Promise<SendStructureSetOutcome>;
    reject(): SendStructureSetOutcome;
    /** Nothing to preview in the viewport; kept for the shared channel. */
    showPreview(): void;
    hidePreview(): void;
}

export type SendStructureSetPlan = MutatingPlan<SendStructureSetProposal>;

interface ParsedInput {
    readonly targetName?: string;
    readonly roiNames?: ReadonlyArray<string>;
}

function readInput(input: unknown): ParsedInput | undefined {
    const o = asRecord(input) ?? {};
    const { targetName, roiNames } = o;
    if (
        targetName !== undefined &&
        (typeof targetName !== 'string' || !targetName)
    ) {
        return undefined;
    }
    if (
        roiNames !== undefined &&
        (!Array.isArray(roiNames) ||
            roiNames.length === 0 ||
            roiNames.length > MAX_STRUCTURES_PER_SEND ||
            !roiNames.every((n) => typeof n === 'string' && !!n))
    ) {
        return undefined;
    }
    return {
        ...(targetName ? { targetName } : {}),
        ...(roiNames ? { roiNames: roiNames as string[] } : {}),
    };
}

/** The Export dialog's default: approved structures if any, else every
 *  structure that is not rejected (useExportSelection). */
function defaultSelection(
    rois: ReadonlyArray<ExportRoiFacts>
): ExportRoiFacts[] {
    const approved = rois.filter((r) => r.isApproved);
    return approved.length ? approved : rois.filter((r) => !r.isRejected);
}

export async function planSendStructureSetToPacs(
    call: MutatingToolCall,
    deps: SendStructureSetDeps
): Promise<SendStructureSetPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    if (!deps.connectorMode) {
        return failedPlan(
            call.toolCallId,
            'sending to a PACS needs the viewer in connector mode'
        );
    }
    const parsed = readInput(call.input);
    if (!parsed) {
        return failedPlan(
            call.toolCallId,
            'invalid send_structure_set_to_pacs input'
        );
    }
    const ss = deps.structureSet();
    if (!ss) {
        return failedPlan(call.toolCallId, 'no structure set is loaded');
    }

    // PACS: the dialog offers only outbound targets.
    let targets: string[];
    try {
        targets = await deps.outboundTargets();
    } catch (err) {
        return failedPlan(
            call.toolCallId,
            `could not read the PACS targets: ${reasonOf(err)}`
        );
    }
    let targetName: string;
    if (parsed.targetName) {
        const match = targets.find(
            (t) => t.toLowerCase() === parsed.targetName!.toLowerCase()
        );
        if (!match) {
            return failedPlan(
                call.toolCallId,
                `"${parsed.targetName}" cannot receive data; outbound PACS: ` +
                    (targets.join(', ') || 'none')
            );
        }
        targetName = match;
    } else if (targets.length === 1) {
        targetName = targets[0];
    } else if (targets.length === 0) {
        return failedPlan(
            call.toolCallId,
            'the connector has no PACS that can receive data'
        );
    } else {
        return failedPlan(
            call.toolCallId,
            'several PACS can receive it; ask the clinician which one: ' +
                targets.join(', ')
        );
    }

    // Structures: the named ones (exact names), or the dialog default.
    let chosen: ExportRoiFacts[];
    const isDefaultSelection = parsed.roiNames === undefined;
    if (parsed.roiNames) {
        const listed: ListedStructure[] = ss.rois.map((r) => ({
            roiNumber: r.roiNumber,
            name: r.name,
            volumeCm3: null,
            type: r.type,
        }));
        chosen = [];
        for (const name of parsed.roiNames) {
            const resolution = resolveRoi(name, listed);
            if (resolution.kind !== 'resolved') {
                return failedPlan(
                    call.toolCallId,
                    `structure "${name}" is not uniquely in "${ss.label}"; ` +
                        'call list_structures and use exact names'
                );
            }
            const roi = ss.rois.find(
                (r) => r.roiNumber === resolution.structure.roiNumber
            );
            if (roi && !chosen.includes(roi)) {
                chosen.push(roi);
            }
        }
    } else {
        chosen = defaultSelection(ss.rois);
    }
    if (chosen.length === 0) {
        return failedPlan(call.toolCallId, 'there are no structures to send');
    }

    const uid = ss.uid;
    const label = ss.label;
    const roiNumbers = chosen.map((r) => r.roiNumber);
    const proposal: SendStructureSetProposal = {
        toolCallId: call.toolCallId,
        structureSetLabel: label,
        targetName,
        structureNames: chosen.map((r) => r.name),
        isDefaultSelection,
        apply: async () => {
            // Refuse if the active set changed while the card waited.
            if (deps.structureSet()?.uid !== uid) {
                return {
                    status: 'failed',
                    reason:
                        'the active structure set changed before this was ' +
                        'applied; re-issue the request',
                };
            }
            const sentBefore = deps.isMarkedSent(uid) === true;
            try {
                await deps.send(uid, roiNumbers, targetName);
            } catch (err) {
                return { status: 'failed', reason: reasonOf(err) };
            }
            const sentAfter = deps.isMarkedSent(uid) === true;
            if (!sentAfter) {
                return {
                    status: 'failed',
                    reason:
                        'the upload did not complete; the viewer shows the ' +
                        'error message',
                };
            }
            return {
                status: 'applied',
                structureSetLabel: label,
                structureCount: roiNumbers.length,
                targetName,
                confirmed: !sentBefore,
            };
        },
        reject: () => ({ status: 'rejected' }),
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
